import { Map, List, fromJS } from 'immutable';

import generateId from '../lib/id_generator';
import { DEFAULT_NOTIFICATION_SETTINGS, EMPTY_TAG_CADENCE } from '../lib/notification_utils';
import { applyCategorySettingsFromConfig } from '../util/settings_persister';

const setLoadingMessage = (state, action) => state.set('loadingMessage', action.loadingMessage);

const hideLoadingMessage = (state) => state.set('loadingMessage', null);

const setFontSize = (state, action) => state.set('fontSize', action.newFontSize);

const setBulletStyle = (state, action) => state.set('bulletStyle', action.newBulletStyle);

const setShouldTapTodoToAdvance = (state, action) =>
  state.set('shouldTapTodoToAdvance', action.newShouldTapTodoToAdvance);

const setAgendaDefaultDeadlineDelayUnit = (state, action) =>
  state.set('agendaDefaultDeadlineDelayUnit', action.newAgendaDefaultDeadlineDelayUnit);

const setAgendaDefaultDeadlineDelayValue = (state, action) =>
  state.set('agendaDefaultDeadlineDelayValue', action.newAgendaDefaultDeadlineDelayValue);

const setEditorDescriptionHeightValue = (state, action) =>
  state.set('editorDescriptionHeightValue', action.newEditorDescriptionHeightValue);

const setAgendaStartOnWeekday = (state, action) =>
  state.set('agendaStartOnWeekday', action.newAgendaStartOnWeekday);

const setShouldStoreSettingsInSyncBackend = (state, action) =>
  state.set('shouldStoreSettingsInSyncBackend', action.newShouldStoreSettingsInSyncBackend);

const setShouldLiveSync = (state, action) => state.set('shouldLiveSync', action.shouldLiveSync);

const setShowDeadlineDisplay = (state, action) =>
  state.set('showDeadlineDisplay', action.showDeadlineDisplay);

const setShouldSyncOnBecomingVisibile = (state, action) =>
  state.set('shouldSyncOnBecomingVisibile', action.shouldSyncOnBecomingVisibile);

const setShouldShowTitleInOrgFile = (state, action) =>
  state.set('shouldShowTitleInOrgFile', action.shouldShowTitleInOrgFile);

const setShouldLogIntoDrawer = (state, action) =>
  state.set('shouldLogIntoDrawer', action.shouldLogIntoDrawer);

const setCloseSubheadersRecursively = (state, action) =>
  state.set('closeSubheadersRecursively', action.closeSubheadersRecursively);

/**
 * When enabled, keep all heading body text flush-left. When disabled (the
 * default) indent the body text of headings according to the nesting level of
 * the heading.
 */
const setShouldNotIndentOnExport = (state, action) =>
  state.set('shouldNotIndentOnExport', action.shouldNotIndentOnExport);

const setHasUnseenChangelog = (state, action) =>
  state.set('hasUnseenChangelog', action.newHasUnseenChangelog);

const setLastSeenChangelogHeader = (state, action) =>
  state.set('lastSeenChangelogHash', action.newLastSeenChangelogHash);

const setLastViewedFile = (state, action) => state.set('lastViewedPath', action.lastViewedPath);

const setCustomKeybinding = (state, action) => {
  if (!state.get('customKeybindings')) {
    state = state.set('customKeybindings', Map());
  }

  return state.setIn(['customKeybindings', action.keybindingName], action.keybinding);
};

const assignNotificationCadenceIds = (state) => {
  const cadences = state.getIn(['notificationSettings', 'tagCadences']);
  if (!cadences) {
    return state;
  }
  return state.updateIn(['notificationSettings', 'tagCadences'], (cs) =>
    cs.map((cadence) => cadence.set('id', generateId()))
  );
};

const ensureNotificationSettings = (state) => {
  if (!state.get('notificationSettings')) {
    return state.set('notificationSettings', DEFAULT_NOTIFICATION_SETTINGS);
  }
  return state;
};

const indexOfCadenceWithId = (cadences, cadenceId) =>
  cadences.findIndex((cadence) => cadence.get('id') === cadenceId);

const updateNotificationSettingsFieldPathValue = (state, action) => {
  state = ensureNotificationSettings(state);
  return state.setIn(['notificationSettings'].concat(action.fieldPath), action.newValue);
};

const addNewEmptyNotificationTagCadence = (state) => {
  state = ensureNotificationSettings(state);
  if (!state.getIn(['notificationSettings', 'tagCadences'])) {
    state = state.setIn(['notificationSettings', 'tagCadences'], List());
  }
  return state.updateIn(['notificationSettings', 'tagCadences'], (cadences) =>
    cadences.push(
      fromJS({
        id: generateId(),
        ...EMPTY_TAG_CADENCE,
      })
    )
  );
};

const updateNotificationTagCadenceFieldPathValue = (state, action) => {
  state = ensureNotificationSettings(state);
  const cadences = state.getIn(['notificationSettings', 'tagCadences']) || List();
  const cadenceIndex = indexOfCadenceWithId(cadences, action.cadenceId);
  if (cadenceIndex === -1) {
    return state;
  }
  return state.setIn(
    ['notificationSettings', 'tagCadences', cadenceIndex].concat(action.fieldPath),
    action.newValue
  );
};

const deleteNotificationTagCadence = (state, action) => {
  const cadences = state.getIn(['notificationSettings', 'tagCadences']);
  if (!cadences) {
    return state;
  }
  const cadenceIndex = indexOfCadenceWithId(cadences, action.cadenceId);
  if (cadenceIndex === -1) {
    return state;
  }
  return state.updateIn(['notificationSettings', 'tagCadences'], (cs) => cs.delete(cadenceIndex));
};

const reorderNotificationTagCadence = (state, action) =>
  state.updateIn(['notificationSettings', 'tagCadences'], (cadences) =>
    cadences.splice(action.fromIndex, 1).splice(action.toIndex, 0, cadences.get(action.fromIndex))
  );

const restoreBaseSettings = (state, action) => {
  if (!action.newSettings) {
    return state;
  }
  return assignNotificationCadenceIds(
    applyCategorySettingsFromConfig(state, action.newSettings, 'base')
  );
};

const pushModalPage = (state, action) =>
  state.update('modalPageStack', (stack) =>
    !!stack ? stack.push(action.modalPage) : List([action.modalPage])
  );

const popModalPage = (state) =>
  state.update('modalPageStack', (stack) => (!!stack ? stack.pop() : stack));

const clearModalStack = (state) => state.set('modalPageStack', List());

const activatePopup = (state, action) => {
  const { data, popupType } = action;

  // Remember active popup in URL state for popups that are uniquely
  // identifiable (aka not related to a single header like tags,
  // properties or timestamps).
  if (['search', 'task-list', 'agenda'].includes(popupType)) {
    window.history.replaceState({}, '', `${window.location.pathname}#${popupType}`);
  }

  return state.set(
    'activePopup',
    fromJS({
      type: popupType,
      data,
    })
  );
};

const closePopup = (state) => {
  window.history.replaceState(
    '',
    document.title,
    window.location.pathname + window.location.search
  );
  return state.set('activePopup', null);
};

const setIsLoading = (state, action) => {
  if (action.isLoading) {
    return state.update('isLoading', (isLoading) => isLoading.add(action.path));
  } else {
    return state.update('isLoading', (isLoading) => isLoading.delete(action.path));
  }
};

const setIsOnline = (state, action) => {
  return state.set('online', action.online);
};

const setAgendaTimeframe = (state, action) => state.set('agendaTimeframe', action.agendaTimeframe);

const setFinderTab = (state, action) => state.set('finderTab', action.finderTab);

const setPreferEditRawValues = (state, action) =>
  state.set('preferEditRawValues', action.preferEditRawValues);

const setOrgHabitShowAllToday = (state, action) =>
  state.set('orgHabitShowAllToday', action.orgHabitShowAllToday);

const setOrgHabitPrecedingDays = (state, action) =>
  state.set('orgHabitPrecedingDays', action.orgHabitPrecedingDays);

const setOrgHabitFollowingDays = (state, action) =>
  state.set('orgHabitFollowingDays', action.orgHabitFollowingDays);

const setColorScheme = (state, action) => {
  return state.set('colorScheme', action.colorScheme);
};

const osColorSchemeChanged = (state) => {
  // Increment a counter to force a re-render in Entry (PureComponent).
  // The colorScheme value stays as 'OS'; loadTheme() re-queries the OS preference.
  return state.update('osColorSchemeChangeCount', (c) => (c || 0) + 1);
};

const setTheme = (state, action) => {
  return state.set('theme', action.theme);
};

/**
 * Reducer that is responsible for the "base" state slice.
 */
export default (state = Map(), action) => {
  switch (action.type) {
    case 'SET_LOADING_MESSAGE':
      return setLoadingMessage(state, action);
    case 'HIDE_LOADING_MESSAGE':
      return hideLoadingMessage(state, action);
    case 'SET_FONT_SIZE':
      return setFontSize(state, action);
    case 'SET_BULLET_STYLE':
      return setBulletStyle(state, action);
    case 'SET_SHOULD_TAP_TODO_TO_ADVANCE':
      return setShouldTapTodoToAdvance(state, action);
    case 'SET_AGENDA_DEFAULT_DEADLINE_DELAY_UNIT':
      return setAgendaDefaultDeadlineDelayUnit(state, action);
    case 'SET_AGENDA_DEFAULT_DEADLINE_DELAY_VALUE':
      return setAgendaDefaultDeadlineDelayValue(state, action);
    case 'SET_EDITOR_DESCRIPTION_HEIGHT_VALUE':
      return setEditorDescriptionHeightValue(state, action);
    case 'SET_AGENDA_START_ON_WEEKDAY':
      return setAgendaStartOnWeekday(state, action);
    case 'SET_SHOULD_STORE_SETTINGS_IN_SYNC_BACKEND':
      return setShouldStoreSettingsInSyncBackend(state, action);
    case 'SET_COLOR_SCHEME':
      return setColorScheme(state, action);
    case 'OS_COLOR_SCHEME_CHANGED':
      return osColorSchemeChanged(state);
    case 'SET_THEME':
      return setTheme(state, action);
    case 'SET_SHOULD_LIVE_SYNC':
      return setShouldLiveSync(state, action);
    case 'SET_SHOW_DEADLINE_DISPLAY':
      return setShowDeadlineDisplay(state, action);
    case 'SET_SHOULD_SYNC_ON_BECOMING_VISIBLE':
      return setShouldSyncOnBecomingVisibile(state, action);
    case 'SET_SHOULD_SHOW_TITLE_IN_ORG_FILE':
      return setShouldShowTitleInOrgFile(state, action);
    case 'SET_SHOULD_LOG_INTO_DRAWER':
      return setShouldLogIntoDrawer(state, action);
    case 'SET_CLOSE_SUBHEADERS_RECURSIVELY':
      return setCloseSubheadersRecursively(state, action);
    case 'SET_SHOULD_NOT_INDENT_ON_EXPORT':
      return setShouldNotIndentOnExport(state, action);
    case 'SET_HAS_UNSEEN_CHANGELOG':
      return setHasUnseenChangelog(state, action);
    case 'SET_LAST_SEEN_CHANGELOG_HEADER':
      return setLastSeenChangelogHeader(state, action);
    case 'SET_LAST_VIEWED_FILE':
      return setLastViewedFile(state, action);
    case 'SET_CUSTOM_KEYBINDING':
      return setCustomKeybinding(state, action);
    case 'RESTORE_BASE_SETTINGS':
      return restoreBaseSettings(state, action);
    case 'PUSH_MODAL_PAGE':
      return pushModalPage(state, action);
    case 'POP_MODAL_PAGE':
      return popModalPage(state, action);
    case 'CLEAR_MODAL_STACK':
      return clearModalStack(state, action);
    case 'ACTIVATE_POPUP':
      return activatePopup(state, action);
    case 'CLOSE_POPUP':
      return closePopup(state, action);
    case 'SET_IS_LOADING':
      return setIsLoading(state, action);
    case 'SET_IS_ONLINE':
      return setIsOnline(state, action);
    case 'SET_AGENDA_TIMEFRAME':
      return setAgendaTimeframe(state, action);
    case 'SET_FINDER_TAB':
      return setFinderTab(state, action);
    case 'PREFER_EDIT_RAW_VALUES':
      return setPreferEditRawValues(state, action);
    case 'SET_ORG_HABIT_SHOW_ALL_TODAY':
      return setOrgHabitShowAllToday(state, action);
    case 'SET_ORG_HABIT_PRECEDING_DAYS':
      return setOrgHabitPrecedingDays(state, action);
    case 'SET_ORG_HABIT_FOLLOWING_DAYS':
      return setOrgHabitFollowingDays(state, action);
    case 'UPDATE_NOTIFICATION_SETTINGS_FIELD_PATH_VALUE':
      return updateNotificationSettingsFieldPathValue(state, action);
    case 'ADD_NEW_EMPTY_NOTIFICATION_TAG_CADENCE':
      return addNewEmptyNotificationTagCadence(state, action);
    case 'UPDATE_NOTIFICATION_TAG_CADENCE_FIELD_PATH_VALUE':
      return updateNotificationTagCadenceFieldPathValue(state, action);
    case 'DELETE_NOTIFICATION_TAG_CADENCE':
      return deleteNotificationTagCadence(state, action);
    case 'REORDER_NOTIFICATION_TAG_CADENCE':
      return reorderNotificationTagCadence(state, action);
    default:
      return state;
  }
};
